import React, { useContext, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { App, Button, Form, Input, Select, Row, Col, Divider, InputNumber } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

import Api from '@vacationtracker/shared/services/api'
import { track } from '../../services/analytics/analytics'
import { notificationStore } from '../../context/notificationsContext/store'
import logger from '@vacationtracker/shared/functions/logger'
import { useManualQuery } from 'graphql-hooks'
import { getAutomationsById } from '../../graphql/custom-queries'
import IntlMessages from '../../util/IntlMessages'
import FilterAdvanced from '@vacationtracker/shared/components/filter-advanced'
import YesNoSwitch from '../yes-no-switch'
import CircularProgress from '../circular-progress'

import { FrontendUrls } from '../../types/urls'
import { IFilter } from '@vacationtracker/shared/types/filter'
import { AddonsStatus, AddonsStatusEnum } from '@vacationtracker/shared/types/addons'
import { ITeamShort } from '@vacationtracker/shared/types/team'
import { IGetTeamsShort } from '../../types/teams'
import { IGetLocationsShort } from '../../types/locations'
import { IGetLabelsShort } from '../../types/labels'
import { IGetAutomationsByIdData, ILeaveTypeListShort, ILeaveRequestNoticePeriodAutomation } from '../../types/custom-queries'
import { IAutomationLeaveRequestNoticePeriodCreateEvent, IAutomationLeaveRequestNoticePeriodForm } from '@vacationtracker/shared/types/automations'
import { ISelected } from '@vacationtracker/shared/components/filter-advanced/types'

const { Option } = Select

interface ILeaveRequestNoticePeriodForm {
  automationId?: string
  teams: IGetTeamsShort[] | ITeamShort[]
  locations: IGetLocationsShort[]
  leaveTypes: ILeaveTypeListShort[]
  labels: IGetLabelsShort[]
  filter: IFilter
  handleFilter: (filter: IFilter) => void
  shouldEnableLabels: boolean
  backToAddons: () => void
  amIAdmin: boolean
  status?: AddonsStatus
}

const LeaveRequestNoticePeriodForm = ({
  automationId,
  teams,
  locations,
  leaveTypes,
  labels,
  filter,
  handleFilter,
  shouldEnableLabels,
  backToAddons,
  amIAdmin,
  status,
}: ILeaveRequestNoticePeriodForm) => {
  const abortControllerRef = useRef<AbortController>(new AbortController())

  const history = useHistory()
  const { formatMessage } = useIntl()
  const [form] = Form.useForm()
  const { notification } = App.useApp()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const [getAutomationsByIdQuery] = useManualQuery<IGetAutomationsByIdData, { id: string }>(getAutomationsById, {
    fetchOptionsOverrides: {
      signal: abortControllerRef.current?.signal,
    },
  })

  const [isLoading, setIsLoading] = useState(true)
  const [btnLoading, setBtnLoading] = useState(false)
  const [applyToAllUsers, setApplyToAllUsers] = useState<boolean>(true)
  const [initialFilterValues, setInitialFilterValues] = useState([{}])
  const [leaveTypeNames, setLeaveTypeNames] = useState<string[]>([])

  const [addon, setAddon] = useState<IAutomationLeaveRequestNoticePeriodForm>({
    automationId: '',
    name: '',
    minLeaveDurationForAdvanceNotice: 21,
    requiredAdvanceNoticeDays: 60,
    autoRejectResponse: formatMessage({
      id: 'automations.leaveDurationLimit.autoRejectResponsePlaceholder',
    }),
    applyToAllUsers: true,
    locationIds: [],
    teamIds: [],
    labelIds: [],
    leaveTypeIds: leaveTypes.map(leaveType => leaveType.id),
  })

  const [formValues, setFormValues] = useState(addon)

  const handleFormChange = (changedValues: any, allValues: any) => {
    setFormValues(allValues)
  }

  useEffect(() => {
    abortControllerRef.current = new AbortController()

    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  useEffect(() => {
    automationId ? getAutomationById(automationId) : setIsLoading(false)
  }, [automationId])

  const getAutomationById = async (id: string) => {
    try {
      const response = await getAutomationsByIdQuery({ variables: { id } })
      const automation = response.data?.getAutomation as ILeaveRequestNoticePeriodAutomation

      const existingAutomation = {
        automationId: automation.id,
        name: automation.name,  
        autoRejectResponse: automation.autoRejectResponse,
        locationIds: automation.locationIds,
        teamIds: automation.teamIds,
        labelIds: automation.labelIds,
        leaveTypeIds: automation.leaveTypeIds,
        minLeaveDurationForAdvanceNotice: automation.minLeaveDurationForAdvanceNotice,
        requiredAdvanceNoticeDays: automation.requiredAdvanceNoticeDays,
      }
      
      handleFilter({
        labelIds: existingAutomation?.labelIds || [],
        locationIds: existingAutomation?.locationIds || [],
        teamIds: existingAutomation?.teamIds || [],
      })

      let applyToAllUsers = true
      if (existingAutomation.locationIds?.length > 0 || existingAutomation.teamIds?.length > 0 || existingAutomation.labelIds?.length > 0) {
        applyToAllUsers = false
        form.setFieldsValue({ applyToAllUsers })
        setApplyToAllUsers(applyToAllUsers)
        setInitialFilterValues(() => {
          const data: ISelected[] = []
          existingAutomation.locationIds?.length > 0 && data.push({ type: 'Locations', values: existingAutomation.locationIds })
          existingAutomation.teamIds?.length > 0 && data.push({ type: 'Departments', values: existingAutomation.teamIds })
          existingAutomation.labelIds?.length > 0 && data.push({ type: 'Labels', values: existingAutomation.labelIds })
          return data
        })
      }
      setAddon({
        ...existingAutomation,
        applyToAllUsers,
      })
      setFormValues({
        ...existingAutomation,
        applyToAllUsers,
      })
      setIsLoading(false)
    } catch (error) {
      console.error('Error fetching automation:', error)
    }
  }

  const changeLeaveTypes = (selectedLeaveTypeIds) => {
    const leaveTypesNames = leaveTypes.map(leaveType => {
      const selectedLeaveTypes = selectedLeaveTypeIds || []
      if (selectedLeaveTypes.includes(leaveType.id)) {
        return leaveType.name
      }
    }).filter(Boolean) as string[] | []
    setLeaveTypeNames(leaveTypesNames)
  }

  const handleApplyToAllUsers = () => {
    setApplyToAllUsers(!applyToAllUsers)
    if(!applyToAllUsers) {
      handleFilter({
        labelIds: [],
        locationIds: [],
        teamIds: [],
      })
    }
  }

  const onFinish = () => {
    if (status === AddonsStatusEnum.CANCELED) return
    form
      .validateFields()
      .then(async (values) => {
        const data = {
          name: values.name,
          minLeaveDurationForAdvanceNotice: values.minLeaveDurationForAdvanceNotice,
          requiredAdvanceNoticeDays: values.requiredAdvanceNoticeDays,
          autoRejectResponse: values.autoRejectResponse,
          leaveTypeIds: values.leaveTypeIds,
          locationIds: filter.locationIds,
          teamIds: amIAdmin ? filter.teamIds : values.teamIds,
          labelIds: filter.labelIds,
        }

        setBtnLoading(true)
        let response
        try {
          track(automationId ? 'LEAVE_REQUEST_NOTICE_PERIOD_UPDATED' : 'LEAVE_REQUEST_NOTICE_PERIOD_CREATED', data)

          if (!automationId) {
            response = await Api.post<Partial<IAutomationLeaveRequestNoticePeriodCreateEvent>>('/core/event', {
              eventType: 'LEAVE_REQUEST_NOTICE_PERIOD_CREATED',
              eventGroup: 'AUTOMATION',
              ...data,
            })
          } else {
            response = await Api.post<Partial<IAutomationLeaveRequestNoticePeriodCreateEvent>>('/core/event', {
              eventType: 'LEAVE_REQUEST_NOTICE_PERIOD_UPDATED',
              eventGroup: 'AUTOMATION',
              automationId,
              ...data,
              isActive: true,
            })
          }

          notification.open({
            key: response.correlationId,
            message: formatMessage({ id: automationId ? 'automations.leaveDurationLimit.updateInProgress' : 'automations.leaveDurationLimit.createInProgress' }),
            icon: (<LoadingOutlined />),
            duration: 0,
          })

          setActionNotifications([ ...actionNotifications, response.correlationId ])

          setTimeout(() => {
            history.push(`${FrontendUrls.addons}/leave-request-notice-period/view`)
          }, 700)
        } catch (error) {
          setBtnLoading(false)
          if (error.response?.data?.error) {
            notification.error({
              message: formatMessage({ id: 'error.automations.automationSubmitError' }),
              description: formatMessage({ id: error.response.data.error }),
              duration: 0,
            })
          } else {
            let description
            let message = formatMessage({ id: 'error.automations.automationSubmitError' })
            if (response?.correlationId) {
              description = formatMessage({ id: 'error.automations.automationSubmitError' })
            } else if (error.message === 'Request failed with status code 400') {
              message = formatMessage({ id: 'error.automations.insufficientPrivileges.title' })
              description = formatMessage({ id: 'error.automations.insufficientPrivileges.description' })
            } else {
              description = error.message
            }

            notification.error({
              message,
              description,
              duration: 0,
            })
          }
        }
      })
      .catch(info => {
        logger.warning('Validate Failed:', info)
        setBtnLoading(false)
      })
  }

  return (
    <>
      {(isLoading && automationId) ?
        <CircularProgress /> :
        <>
          <Form
            form={form}
            layout="vertical"
            name="automationsForm"
            disabled={status === AddonsStatusEnum.CANCELED}
            onFinish={onFinish}
            className="automation-form"
            initialValues={addon}
            scrollToFirstError
            requiredMark="optional"
            labelWrap
            onValuesChange={handleFormChange}
            size="large"
          >
            <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
              <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                <h2><IntlMessages id="automations.leaveRequestNoticePeriod.reduceLastMinuteAbsences" /></h2>
                <p><IntlMessages id="automations.leaveRequestNoticePeriod.reduceLastMinuteAbsencesDescription" /></p>
              </Col>
              <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                <Form.Item
                  name="name"
                  label={<IntlMessages id="automations.policyName" />}
                  rules={[
                    {
                      required: true,
                      message: <IntlMessages id="form.nameRequired" />,
                    },
                  ]}
                >
                  <Input
                    placeholder={formatMessage({ id: 'automationsForm.policy.name' })}
                    disabled={status === AddonsStatusEnum.CANCELED}
                  />
                </Form.Item>
                <Form.Item className="automation-form-item-inline">
                  <span><IntlMessages id="automationsForm.leaveLongerThan" /></span>
                  <Form.Item
                    style={{ marginBottom: 0, marginLeft: 10, marginRight: 10 }}
                    name="minLeaveDurationForAdvanceNotice"
                    rules={[
                      {
                        required: true,
                        message: '',
                      },
                    ]}
                  >
                    <InputNumber min={1} disabled={status === AddonsStatusEnum.CANCELED}/>
                  </Form.Item>
                  <span><IntlMessages id="automationsForm.daysMustBeRequestedInAdvance" /></span>
                  <Form.Item
                    style={{ marginBottom: 0, marginLeft: 10, marginRight: 10 }}
                    name="requiredAdvanceNoticeDays"
                    rules={[
                      {
                        required: true,
                        message: '',
                      },
                    ]}
                  >
                    <InputNumber min={1} disabled={status === AddonsStatusEnum.CANCELED}/>
                  </Form.Item>
                  <span><IntlMessages id="automationsForm.daysInAdvance" /></span>
                </Form.Item>

                <Form.Item
                  name="autoRejectResponse"
                  label={<IntlMessages id="automations.autoRejectResponse" />}
                  rules={[
                    {
                      required: true,
                      message: <IntlMessages id="automationsForm.autoRejectResponseRequired" />,
                    },
                  ]}
                >
                  <Input.TextArea
                    disabled={status === AddonsStatusEnum.CANCELED}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Divider />
            <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
              <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                <h2><IntlMessages id="app.applyTo" /></h2>
                <p><IntlMessages id="automations.filterDescription" /></p>
                <p><IntlMessages id="automations.leaveTypesDescription" /></p>
              </Col>
              <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                <YesNoSwitch
                  name="applyToAllUsers"
                  tooltip={<IntlMessages id="automationsForm.applyToAllUsersTooltip" />}
                  defaultValue={applyToAllUsers}
                  onChange={handleApplyToAllUsers}
                  label={<IntlMessages id="automationsForm.applyToAllUsers" />}
                  disabled={status === AddonsStatusEnum.CANCELED}
                />
                {!applyToAllUsers && amIAdmin &&
                  <>
                    <FilterAdvanced
                      isForm={true}
                      data={{
                        Locations: locations,
                        Departments: teams,
                        Labels: labels,
                      }}
                      onChangeFilter={handleFilter}
                      initialValues={initialFilterValues}
                      showLabels={shouldEnableLabels}
                      toolTipInfo="automationsForm.advancedFilter.tooltipInfo"
                      filterByWidth={125}
                      saveFilters={false}
                      page={automationId ? undefined : 'automationsForm'}
                    />
                  </>
                }
                {!amIAdmin &&
                  <Form.Item
                    label={<IntlMessages id="calendar.filterByTeam" />}
                    rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
                    name="teamIds"
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      style={{ width: '100%' }}
                      placeholder={<IntlMessages id="app.pleaseSelect" />}
                    >
                      {teams.map((team) => (
                        <Option value={team.id} key={team.id}>
                          {team.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                }
                <Form.Item
                  label={<IntlMessages id="app.leaveTypes" />}
                  rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
                  name="leaveTypeIds"
                >
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    placeholder={<IntlMessages id="app.pleaseSelect" />}
                    onChange={changeLeaveTypes}
                    disabled={status === AddonsStatusEnum.CANCELED}
                  >
                    {leaveTypes.map((leaveType) => (
                      <Option value={leaveType.id} key={leaveType.id}>
                        {leaveType.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="units" hidden>
              <Input />
            </Form.Item>
            
            <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
              <Col span={24}>
                <Form.Item style={{ textAlign: 'right' }}>
                  <Button onClick={backToAddons} style={{ marginRight: '10px' }}>
                    <IntlMessages id="app.cancel" />
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={btnLoading}
                    disabled={status === AddonsStatusEnum.CANCELED}
                  >
                    <IntlMessages id="app.save" />
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Row>
            <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
            </Col>
            <Col xs={24} sm={24} md={16} lg={16} xl={12}>
              <div className='vt-primary-color' style={{ textAlign: 'center' }}>
                <IntlMessages id="automations.leaveRequestNoticePeriod.summary"
                  values={{
                    policyName: formValues.name || '',
                    leaveTypes: leaveTypes.filter(lt => formValues.leaveTypeIds?.includes(lt.id))?.map(lt => lt.name)?.join(', ') || <IntlMessages id="app.all" />,
                    minLeaveDurationForAdvanceNotice: formValues.minLeaveDurationForAdvanceNotice,
                    requiredAdvanceNoticeDays: formValues.requiredAdvanceNoticeDays,
                    allUsers: applyToAllUsers ? 2 : 1,
                  }} />
              </div>
            </Col>
          </Row>
        </>
      }
    </>
  )
}

export default LeaveRequestNoticePeriodForm